// src/pages/actividades/components/BulkActionBar.tsx
import { useState, useRef } from 'react';
import { Button } from '@/components/ui';
import { CalendarDays, ArrowRight, Check, Trash2, X, ChevronDown, Calendar, Archive } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { PrioridadActividad, AreaActividad } from '@/types';

interface BulkActionBarProps {
  count: number;
  onMoveDate: (fecha: string) => void;
  onChangePriority: (prioridad: PrioridadActividad) => void;
  onChangeArea: (area: AreaActividad) => void;
  onMoveToBacklog: () => void;
  onRemoveFromBacklog: () => void;
  onCompleteAll: () => void;
  onDeleteAll: () => void;
  onClearSelection: () => void;
  isPending?: boolean;
  isBacklogTab?: boolean;
}

function getDateLabel(offset: number): { label: string; date: string } {
  const d = new Date();
  d.setDate(d.getDate() + offset);
  const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const dayName = d.toLocaleDateString('es-MX', { weekday: 'short', day: 'numeric', month: 'short' });
  return { label: dayName, date: iso };
}

function toISO(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function getNextMonday(): { label: string; date: string } {
  const d = new Date();
  const day = d.getDay();
  const daysUntilMonday = day === 0 ? 1 : 8 - day;
  d.setDate(d.getDate() + daysUntilMonday);
  return { label: `Lun ${d.getDate()}/${d.getMonth() + 1}`, date: toISO(d) };
}

function getNextSaturday(): { label: string; date: string } {
  const d = new Date();
  const day = d.getDay();
  const daysUntilSat = day === 6 ? 7 : 6 - day;
  d.setDate(d.getDate() + daysUntilSat);
  return { label: `Sáb ${d.getDate()}/${d.getMonth() + 1}`, date: toISO(d) };
}

function getNextWeekMonday(): { label: string; date: string } {
  const d = new Date();
  const day = d.getDay();
  // Mon after this coming Monday: Sun=8, Mon=14, Tue=13, Wed=12, Thu=11, Fri=10, Sat=9
  const daysUntilNextWeekMon = day === 0 ? 8 : (day === 1 ? 14 : 15 - day);
  d.setDate(d.getDate() + daysUntilNextWeekMon);
  return { label: `Lun ${d.getDate()}/${d.getMonth() + 1}`, date: toISO(d) };
}

function getNextMonthFirst(): { label: string; date: string } {
  const d = new Date();
  d.setMonth(d.getMonth() + 1, 1);
  return { label: `1/${d.getMonth() + 1}/${d.getFullYear()}`, date: toISO(d) };
}

const PRIORIDADES: { value: PrioridadActividad; label: string; color: string }[] = [
  { value: 'AAA', label: 'AAA', color: 'text-red-600' },
  { value: 'AA', label: 'AA', color: 'text-orange-600' },
  { value: 'A', label: 'A', color: 'text-yellow-600' },
  { value: 'B', label: 'B', color: 'text-blue-600' },
  { value: 'C', label: 'C', color: 'text-gray-500' },
];

const AREAS: { value: AreaActividad; label: string }[] = [
  { value: 'TRABAJO', label: 'Work' },
  { value: 'ADMINISTRATIVO', label: 'Admin' },
  { value: 'PERSONAL', label: 'Personal' },
];

type OpenDropdown = 'fecha' | 'prioridad' | 'area' | null;

export function BulkActionBar({
  count,
  onMoveDate,
  onChangePriority,
  onChangeArea,
  onMoveToBacklog,
  onRemoveFromBacklog,
  onCompleteAll,
  onDeleteAll,
  onClearSelection,
  isPending,
  isBacklogTab,
}: BulkActionBarProps) {
  const [openDropdown, setOpenDropdown] = useState<OpenDropdown>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const dateInputRef = useRef<HTMLInputElement>(null);

  const today = getDateLabel(0);
  const tomorrow = getDateLabel(1);
  const monday = getNextMonday();
  const saturday = getNextSaturday();
  const nextWeek = getNextWeekMonday();
  const nextMonth = getNextMonthFirst();

  const dateOptions = [
    { label: 'Hoy', date: today.date },
    { label: `Mañana (${tomorrow.label})`, date: tomorrow.date },
    { label: `Lunes (${monday.label})`, date: monday.date },
    { label: `Fin de semana (${saturday.label})`, date: saturday.date },
    { label: `Próx. semana (${nextWeek.label})`, date: nextWeek.date },
    { label: `Próx. mes (${nextMonth.label})`, date: nextMonth.date },
  ];

  const toggleDropdown = (dropdown: OpenDropdown) => {
    setOpenDropdown(openDropdown === dropdown ? null : dropdown);
  };

  const handleMoveDate = (fecha: string) => {
    onMoveDate(fecha);
    setOpenDropdown(null);
  };

  const handleChangePriority = (prioridad: PrioridadActividad) => {
    onChangePriority(prioridad);
    setOpenDropdown(null);
  };

  const handleChangeArea = (area: AreaActividad) => {
    onChangeArea(area);
    setOpenDropdown(null);
  };

  return (
    <div className="flex items-center gap-2 px-4 py-2.5 bg-primary/5 border border-primary/20 rounded-lg">
      <span className="text-sm font-medium text-primary shrink-0">
        {count} seleccionada{count > 1 ? 's' : ''}
      </span>

      <div className="h-4 w-px bg-border mx-1" />

      {/* Mover a fecha */}
      <div className="relative">
        <Button
          variant="outline"
          size="sm"
          onClick={() => toggleDropdown('fecha')}
          disabled={isPending}
          className="h-7 text-xs gap-1"
        >
          <ArrowRight className="h-3 w-3" />
          Mover a...
          <ChevronDown className="h-3 w-3" />
        </Button>
        {openDropdown === 'fecha' && (
          <div className="absolute top-full left-0 mt-1 z-50 bg-popover border rounded-md shadow-md py-1 min-w-[220px]">
            {dateOptions.map((opt) => (
              <button
                key={opt.date}
                onClick={() => handleMoveDate(opt.date)}
                className="w-full text-left px-3 py-1.5 text-sm hover:bg-accent transition-colors"
              >
                <CalendarDays className="h-3 w-3 inline mr-2 text-muted-foreground" />
                {opt.label}
              </button>
            ))}
            <div className="border-t my-1" />
            <div className="px-3 py-1.5 flex items-center gap-2">
              <Calendar className="h-3 w-3 text-muted-foreground shrink-0" />
              <input
                ref={dateInputRef}
                type="date"
                onChange={(e) => {
                  if (e.target.value) {
                    handleMoveDate(e.target.value);
                    e.target.value = '';
                  }
                }}
                className="h-6 flex-1 text-sm border rounded px-1.5 bg-background focus:outline-none focus:ring-1 focus:ring-ring"
              />
            </div>
            {!isBacklogTab && (
              <>
                <div className="border-t my-1" />
                <button
                  onClick={() => { onMoveToBacklog(); setOpenDropdown(null); }}
                  className="w-full text-left px-3 py-1.5 text-sm hover:bg-accent transition-colors text-muted-foreground"
                >
                  <Archive className="h-3 w-3 inline mr-2" />
                  Algún día...
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Restaurar from backlog */}
      {isBacklogTab && (
        <Button
          variant="outline"
          size="sm"
          onClick={onRemoveFromBacklog}
          disabled={isPending}
          className="h-7 text-xs gap-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
        >
          <ArrowRight className="h-3 w-3" />
          Restaurar
        </Button>
      )}

      {/* Cambiar prioridad */}
      <div className="relative">
        <Button
          variant="outline"
          size="sm"
          onClick={() => toggleDropdown('prioridad')}
          disabled={isPending}
          className="h-7 text-xs gap-1"
        >
          Prioridad
          <ChevronDown className="h-3 w-3" />
        </Button>
        {openDropdown === 'prioridad' && (
          <div className="absolute top-full left-0 mt-1 z-50 bg-popover border rounded-md shadow-md py-1 min-w-[100px]">
            {PRIORIDADES.map((p) => (
              <button
                key={p.value}
                onClick={() => handleChangePriority(p.value)}
                className={cn(
                  'w-full text-left px-3 py-1.5 text-sm hover:bg-accent transition-colors font-medium',
                  p.color
                )}
              >
                {p.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Cambiar area */}
      <div className="relative">
        <Button
          variant="outline"
          size="sm"
          onClick={() => toggleDropdown('area')}
          disabled={isPending}
          className="h-7 text-xs gap-1"
        >
          Área
          <ChevronDown className="h-3 w-3" />
        </Button>
        {openDropdown === 'area' && (
          <div className="absolute top-full left-0 mt-1 z-50 bg-popover border rounded-md shadow-md py-1 min-w-[130px]">
            {AREAS.map((a) => (
              <button
                key={a.value}
                onClick={() => handleChangeArea(a.value)}
                className="w-full text-left px-3 py-1.5 text-sm hover:bg-accent transition-colors"
              >
                {a.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Completar todas */}
      <Button
        variant="outline"
        size="sm"
        onClick={onCompleteAll}
        disabled={isPending}
        className="h-7 text-xs gap-1 text-green-600 hover:text-green-700 hover:bg-green-50"
      >
        <Check className="h-3 w-3" />
        Completar
      </Button>

      {/* Eliminar (with confirmation) */}
      {confirmDelete ? (
        <>
          <span className="text-xs text-red-600 font-medium">
            Eliminar {count}?
          </span>
          <Button
            variant="destructive"
            size="sm"
            onClick={() => { onDeleteAll(); setConfirmDelete(false); }}
            disabled={isPending}
            className="h-7 text-xs"
          >
            Confirmar
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setConfirmDelete(false)}
            className="h-7 text-xs"
          >
            Cancelar
          </Button>
        </>
      ) : (
        <Button
          variant="outline"
          size="sm"
          onClick={() => setConfirmDelete(true)}
          disabled={isPending}
          className="h-7 text-xs gap-1 text-red-600 hover:text-red-700 hover:bg-red-50"
        >
          <Trash2 className="h-3 w-3" />
          Eliminar
        </Button>
      )}

      <div className="flex-1" />

      {/* Deseleccionar */}
      <Button
        variant="ghost"
        size="sm"
        onClick={onClearSelection}
        className="h-7 text-xs gap-1"
      >
        <X className="h-3 w-3" />
        Deseleccionar
      </Button>
    </div>
  );
}
